import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import OrderOffers from '../components/OrderOffers';
import Avatar from '../components/Avatar';
import { apiFetch } from '../api';
import { Loading, ErrorBanner, Empty, Badge, ConfirmDialog, Toast, ui, fmtDate } from '../components/adminUi';
import { formatPhone } from '../lib/phone';
import { formatSom } from '../lib/money';
import { formatOrderDescription } from '../lib/orderDescription';
import { orderStatusLabel, orderStatusTone } from '../lib/orderStatus';

// Buyurtma tafsiloti: GET /api/admin/orders/:id (404 { message: 'Buyurtma topilmadi' }),
// holatni o'zgartirish: PATCH /api/admin/orders/:id/status (oldingi modal bilan bir xil).
export default function OrderDetailPage() {
  const { id } = useParams();
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const [newStatus, setNewStatus] = useState('');
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [changing, setChanging] = useState(false);
  const [toast, setToast] = useState(null);
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const res = await apiFetch(`/api/admin/orders/${encodeURIComponent(id)}`, auth.token);
      if (seq !== reqSeq.current) return;
      if (res.status === 404) {
        setOrder(null);
        setNotFound(true);
        return;
      }
      if (!res.ok) throw new Error();
      setOrder(await res.json());
    } catch {
      if (seq !== reqSeq.current) return;
      setOrder(null);
      setError("Buyurtmani yuklab bo'lmadi.");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [auth.token, id]);

  useEffect(() => {
    setNewStatus('');
    load();
  }, [load]);

  const changeStatus = async () => {
    if (!order || !newStatus) return;
    setChanging(true);
    try {
      const res = await apiFetch(`/api/admin/orders/${order.id}/status`, auth.token, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setToast({ type: 'success', text: `Holat o'zgartirildi: ${orderStatusLabel(newStatus)}` });
        if (d && d.id) setOrder(d);
        setNewStatus('');
      } else {
        setToast({ type: 'error', text: d.message || "Holatni o'zgartirishda xatolik yuz berdi." });
      }
    } catch {
      setToast({ type: 'error', text: "Tarmoq xatosi. Qayta urinib ko'ring." });
    } finally {
      setChanging(false);
      setConfirmStatus(false);
    }
  };

  const back = (
    <button type="button" style={d.back} onClick={() => navigate('/admin/orders')}>
      ← Buyurtmalar
    </button>
  );

  if (loading) return <AdminLayout>{back}<Loading /></AdminLayout>;

  if (notFound) {
    return (
      <AdminLayout>
        {back}
        <Empty icon="package" title="Buyurtma topilmadi" hint="Bu buyurtma mavjud emas yoki o'chirilgan." />
      </AdminLayout>
    );
  }

  if (error || !order) {
    return (
      <AdminLayout>
        {back}
        <ErrorBanner>
          <span style={d.errorRow}>
            <span>{error || "Buyurtmani yuklab bo'lmadi."}</span>
            <button type="button" style={{ ...ui.btnSm, ...ui.btnRed }} onClick={load}>Qayta urinish</button>
          </span>
        </ErrorBanner>
      </AdminLayout>
    );
  }

  const offers = Array.isArray(order.offers) ? order.offers : [];
  const allowed = Array.isArray(order.allowedStatuses) ? order.allowedStatuses : [];

  return (
    <AdminLayout>
      {back}
      <div style={d.head}>
        <h1 style={d.title}>Buyurtma #{order.id}</h1>
        <Badge tone={orderStatusTone(order.status)}>{orderStatusLabel(order.status)}</Badge>
      </div>

      <div style={d.card}>
        <Row label="Kategoriya" value={order.categoryName} />
        <Row label="Manzil" value={order.address} />
        <Row label="Hudud" value={order.regionName || order.region} />
        <Row label="Narx" value={order.price != null ? formatSom(order.price) : null} />
        <Row label="Byudjet" value={order.budget != null ? formatSom(order.budget) : null} />
        <Row label="Yaratilgan" value={order.createdAt ? fmtDate(order.createdAt, true) : null} />
        <Row label="Yakunlangan" value={order.completedAt ? fmtDate(order.completedAt, true) : null} />
        <div style={{ ...d.row, flexDirection: 'column', gap: '6px', borderBottom: 'none' }}>
          <span style={d.rowLabel}>Tavsif</span>
          <span style={{ ...d.rowValue, textAlign: 'left' }}>{formatOrderDescription(order.description) || '—'}</span>
        </div>
      </div>

      <div className="th-grid-2" style={d.parties}>
        <Party title="Mijoz" party={order.client} fallbackName={order.clientName} />
        <Party
          title="Usta"
          party={order.provider}
          fallbackName={order.providerName}
          emptyText="Usta hali tanlanmagan"
          onOpen={order.provider && order.provider.id ? () => navigate(`/admin/providers/${order.provider.id}`) : undefined}
        />
      </div>

      <h2 style={{ ...ui.sectionTitle, marginTop: '24px' }}>
        Takliflar{offers.length ? ` (${offers.length})` : ''}
      </h2>
      <div style={{ maxWidth: '640px' }}>
        <OrderOffers offers={offers} />
      </div>

      <div style={d.statusBox}>
        <div style={ui.label}>Holatni o'zgartirish</div>
        {allowed.length > 0 ? (
          <div style={d.statusRow}>
            <select
              style={{ ...ui.select, flex: '1 1 200px', minWidth: 0 }}
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={changing}
              aria-label="Yangi holat"
            >
              <option value="">Yangi holatni tanlang</option>
              {allowed.map((st) => (
                <option key={st} value={st}>{orderStatusLabel(st)}</option>
              ))}
            </select>
            <button
              type="button"
              style={{ ...ui.saveBtn, ...(!newStatus || changing ? ui.btnDisabled : {}) }}
              onClick={() => setConfirmStatus(true)}
              disabled={!newStatus || changing}
            >
              O'zgartirish
            </button>
          </div>
        ) : (
          <div style={ui.pagerInfo}>Bu holatdagi buyurtmaning holatini o'zgartirib bo'lmaydi.</div>
        )}
      </div>

      {confirmStatus && newStatus && (
        <ConfirmDialog
          title="Holatni o'zgartirish"
          message={`Buyurtma #${order.id} holati «${orderStatusLabel(order.status)}» dan «${orderStatusLabel(newStatus)}» ga o'zgartirilsinmi?`}
          confirmLabel="O'zgartirish"
          busy={changing}
          onCancel={() => setConfirmStatus(false)}
          onConfirm={changeStatus}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </AdminLayout>
  );
}

function Row({ label, value }) {
  return (
    <div style={d.row}>
      <span style={d.rowLabel}>{label}</span>
      <span style={d.rowValue}>{value === null || value === undefined || value === '' ? '—' : value}</span>
    </div>
  );
}

// Mijoz / usta kartochkasi: rasm + ism + telefon (backend `client` / `provider`: {id, name, phone, imageUrl}).
function Party({ title, party, fallbackName, emptyText = "Ma'lumot yo'q", onOpen }) {
  const name = (party && party.name) || fallbackName;
  const body = (
    <>
      <div style={d.partyLabel}>{title}</div>
      {name ? (
        <div style={d.partyRow}>
          <Avatar
            src={party && party.imageUrl}
            firstName={party && party.firstName ? party.firstName : name}
            lastName={party && party.lastName}
            size={40}
          />
          <div style={{ minWidth: 0 }}>
            <div style={d.partyName}>{name}</div>
            <div style={d.partyPhone}>{party && party.phone ? formatPhone(party.phone) : '—'}</div>
          </div>
        </div>
      ) : (
        <div style={d.partyPhone}>{emptyText}</div>
      )}
    </>
  );
  if (!onOpen) return <div style={d.party}>{body}</div>;
  return (
    <button type="button" style={{ ...d.party, ...d.partyBtn }} onClick={onOpen}>
      {body}
    </button>
  );
}

const d = {
  back: {
    background: 'transparent',
    border: 'none',
    padding: '8px 0',
    marginBottom: '12px',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--color-text)',
    cursor: 'pointer',
  },
  head: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' },
  title: { margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--color-text)' },
  card: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '4px 16px',
    maxWidth: '640px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '14px',
  },
  rowLabel: { color: 'var(--color-text-muted)', flexShrink: 0 },
  rowValue: { color: 'var(--color-text)', fontWeight: '600', textAlign: 'right', minWidth: 0, wordBreak: 'break-word' },
  parties: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxWidth: '640px', marginTop: '8px' },
  party: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '16px',
    minWidth: 0,
    textAlign: 'left',
    border: 'none',
    fontFamily: 'var(--font-family)',
  },
  partyBtn: { cursor: 'pointer' },
  partyLabel: { fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '10px' },
  partyRow: { display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 },
  partyName: { fontSize: '14px', fontWeight: '700', color: 'var(--color-text)', wordBreak: 'break-word' },
  partyPhone: { fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '2px' },
  statusBox: {
    marginTop: '24px',
    padding: '16px',
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    maxWidth: '640px',
  },
  statusRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  errorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' },
};
